import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
} from 'root/lib/SearchlightDispatcher';
import { logRumAction } from 'lib/DDRum';
import { getGamesForDisplay, getCurrentQuestId } from 'a/hub/selectors/workshopSelectors';
import { advanceToNextWorkshopStep } from 'a/hub/actions/workshopActions';
import { findGameInfo } from '../../../../../../../lib/utilityFunctions';
import createActionObj from '../../../../../../actions/createActionObj';
import goToActivity from '../../../../../../actions/navigationActions';
import GameIds from '../../../../../data/GameIds';
import useWorkshopSfx from '../../../hooks/useWorkshopSfx';
import GameIcon from '../../games/GameIcon';
import { ModalContainer } from '../sharedComponents';
import WideBackground from '../svgs/Common/WideBackground.svg';
import {
  CloseModalButton,
  LargeBackground,
  SkillGamesIcon,
  DecolineUpper,
  DecolineLower,
  GoButton,
} from './styledComponents';
import SkillGames from './svgs/SkillGames.svg';
import DecolineUpperIcon from './svgs/DecolineUpper.svg';
import DecolineLowerIcon from './svgs/DecolineLower.svg';
import {
  getDefaultSkillId,
  isValidGameSelection,
  resolveSelectedGame,
} from './gameSelectorModalUtils';

const GameSelectorModal = ({ closeModal }) => {
  const dispatch = useDispatch();
  const games = useSelector(getGamesForDisplay);
  const activeQuestId = useSelector(getCurrentQuestId);

  const [gameSelectedSkillId, setGameSelectedSkillId] = useState(() => getDefaultSkillId(games));

  const { playTap } = useWorkshopSfx();
  const clickedRef = useRef(false);
  const timeout = useRef(null);
  const { playSfxFromMap, audioMap } = useWorkshopSfx();

  useEffect(() => {
    setGameSelectedSkillId((currentSkillId) => {
      if (isValidGameSelection(games, currentSkillId)) {
        return currentSkillId;
      }
      const resolvedSkillId = getDefaultSkillId(games);
      logRumAction('GameSelectorModal: stale game selection resynced', {
        previousSkillId: currentSkillId,
        resolvedSkillId,
        availableSkillIds: games.map(({ skillId }) => skillId),
      });
      return resolvedSkillId;
    });
  }, [games]);

  useEffect(() => {
    // Check if all games are completed
    const areAllGamesComplete = games.every(
      ({ levelsMastered, levels }) => levelsMastered.length >= levels.length,
    );

    if (areAllGamesComplete) {
      closeModal();
      const source = 'GameSelectorModal';
      dispatch(advanceToNextWorkshopStep(source));
    } else {
      playSfxFromMap(audioMap.skill_game_launch);
    }

    return () => clearTimeout(timeout.current);
  }, []);

  const onCloseButtonPress = () => {
    if (clickedRef.current) return;
    playTap(true);
    closeModal();
  };

  const onSelectGame = (skillId) => {
    if (clickedRef.current) return;
    playTap(true);
    setGameSelectedSkillId(skillId);
  };

  const launchSelectedGame = (selectedGame) => {
    const gameLaunchedFrom = {
      location: undefined,
      questId: activeQuestId.toString(),
      isQuestWorkflow: true,
    };

    dispatch(
      createActionObj(GameIds.METAGAME, 'SEARCHLIGHT_DISPATCH_EVENT', {
        gameId: GameIds.METAGAME,
        application: {
          gameId: selectedGame.game,
          eventType: 'MINIGAME_LAUNCHED',
          details: gameLaunchedFrom,
        },
      }),
    );
    const navigationParams = {
      destinationGroup: DESTINATION_GROUP.GAME,
      origin: NAVIGATION_ORIGIN.WORKSHOP,
      trigger: 'gameLauncher',
      triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
    };
    dispatch(
      goToActivity({
        activityName: selectedGame.game,
        isQuestWorkflow: true,
        params: {
          questLevels: selectedGame.levels,
          questLevelsMastered: selectedGame.levelsMastered,
          location: undefined,
          game: selectedGame.game,
          questId: activeQuestId,
          skillId: selectedGame.skillId,
        },
        navigationParams,
      }),
    );
  };

  const selectedGame = resolveSelectedGame(games, gameSelectedSkillId);
  const canLaunchGame = Boolean(selectedGame);

  const onGoButtonPress = () => {
    playTap();
    if (clickedRef.current || !selectedGame) return;

    clickedRef.current = true;
    timeout.current = setTimeout(() => {
      closeModal();
      launchSelectedGame(selectedGame);
    }, 150);
  };

  return (
    <StyledContainer
      role="dialog"
      aria-modal="true"
      aria-labelledby="game-selection-modal-title"
      data-dd-action-name="game-selector-modal"
    >
      <CloseModalButton
        onClick={onCloseButtonPress}
        data-testid="game-selection-modal-close-button"
        data-dd-action-name="game-selector-modal:close"
      />
      <h2 className="sr-only" id="game-selection-modal-title">Select Next Game</h2>
      <LargeBackground src={WideBackground} alt="" />
      <SkillGamesIcon src={SkillGames} alt="" />
      <DecolineUpper src={DecolineUpperIcon} alt="" />
      <DecolineLower src={DecolineLowerIcon} alt="" />
      {activeQuestId &&
        games.map(({ game, skillId, levelsMastered, levels }, index) => {
          const [{ canonicalName, displayName }] = findGameInfo(game);
          return (
            <StyledIconContainer margin={games.length < 3 ? '0 10px' : 0} key={`game${index + 1}`}>
              <GameIcon
                game={{
                  canonicalName,
                  displayName,
                  skillId,
                }}
                onSelectGame={onSelectGame}
                completed={levelsMastered.length >= levels.length}
                isStarted={levelsMastered.length !== 0 && levelsMastered.length < levels.length}
                selectedGameSkillId={gameSelectedSkillId}
                actionName={`game-selector-modal:${canonicalName}`}
              />
            </StyledIconContainer>
          );
        })}
      <GoButton
        ariaLabel="Launch Selected Game"
        disabled={!canLaunchGame}
        onClick={onGoButtonPress}
        actionName="game-selector-modal:go"
      />
    </StyledContainer>
  );
};

GameSelectorModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default GameSelectorModal;

const StyledContainer = styled(ModalContainer)`
  height: 558px;
`;

const StyledIconContainer = styled.div`
  width: 180px;
  height: 180px;
  margin: ${({ margin }) => margin || '0'};
`;
